import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_BASE = `${BACKEND_URL}/api/admin`;

export const adminApi = axios.create({
    baseURL: ADMIN_BASE,
    headers: { "Content-Type": "application/json" },
    timeout: 30000,
});

adminApi.interceptors.request.use((config) => {
    const token = localStorage.getItem("admin_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

adminApi.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response?.status === 401 && typeof window !== "undefined") {
            const path = window.location.pathname;
            if (path !== "/admin/login") {
                localStorage.removeItem("admin_token");
                localStorage.removeItem("admin_user");
                window.location.href = "/admin/login";
            }
        }
        return Promise.reject(err);
    },
);

export const login = async (email, password) => {
    const { data } = await adminApi.post("/login", { email, password });
    return data;
};
export const me = () => adminApi.get("/me").then((r) => r.data);
export const dashboard = () => adminApi.get("/dashboard").then((r) => r.data);
export const listLeads = (params = {}) =>
    adminApi.get("/leads", { params }).then((r) => r.data);
export const leadDetail = (id) =>
    adminApi.get(`/leads/${id}`).then((r) => r.data);
export const patchLead = (id, body) =>
    adminApi.patch(`/leads/${id}`, body).then((r) => r.data);
export const patchStatus = (id, estatus) =>
    adminApi.patch(`/leads/${id}/status`, { estatus }).then((r) => r.data);
export const loseLead = (id, reason, note) =>
    adminApi.post(`/leads/${id}/lose`, { reason, note }).then((r) => r.data);
export const addNote = (id, nota) =>
    adminApi.post(`/leads/${id}/notes`, { nota }).then((r) => r.data);
export const deleteLead = (id) =>
    adminApi.delete(`/leads/${id}`).then((r) => r.data);
export const pipeline = (params = {}) =>
    adminApi.get("/pipeline", { params }).then((r) => r.data);
export const rescoreAll = () =>
    adminApi.post("/leads/rescore-all").then((r) => r.data);
export const lossReasons = () =>
    adminApi.get("/constants/loss-reasons").then((r) => r.data);

export const exportCsvUrl = (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return `${ADMIN_BASE}/leads/export/csv${q ? "?" + q : ""}`;
};

export const downloadCsv = async (params = {}) => {
    const res = await adminApi.get("/leads/export/csv", {
        params,
        responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "eclipse_leads.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const SITIO_BADGE = {
    eclipse: { label: "Eclipse", color: "bg-neutral-800 text-white" },
    flexshade: { label: "FlexShade", color: "bg-eclipse-cta text-white" },
    velarias: { label: "Velarias", color: "bg-indigo-700 text-white" },
};

export const TEMP_BADGE = {
    hot: { label: "HOT", color: "bg-red-100 text-red-700 border-red-200" },
    warm: { label: "WARM", color: "bg-amber-100 text-amber-700 border-amber-200" },
    cold: { label: "COLD", color: "bg-blue-100 text-blue-700 border-blue-200" },
};

export const STATUS_OPTIONS = [
    { value: "nuevo", label: "Nuevo", color: "bg-neutral-100 text-neutral-700 border-neutral-200" },
    { value: "contactado", label: "Contactado", color: "bg-sky-100 text-sky-700 border-sky-200" },
    { value: "seguimiento", label: "Seguimiento", color: "bg-blue-100 text-blue-700 border-blue-200" },
    { value: "cotizacion_enviada", label: "Cotización enviada", color: "bg-purple-100 text-purple-700 border-purple-200" },
    { value: "visita_agendada", label: "Visita agendada", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
    { value: "negociacion", label: "Negociación", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    { value: "anticipo_recibido", label: "Anticipo recibido", color: "bg-orange-100 text-orange-700 border-orange-200" },
    { value: "ganado", label: "Ganado", color: "bg-green-100 text-green-700 border-green-200" },
    { value: "perdido", label: "Perdido", color: "bg-red-100 text-red-700 border-red-200" },
];

export const FUENTE_OPTIONS = [
    { value: "cotizacion", label: "Cotización" },
    { value: "catalogo", label: "Catálogo" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "directo", label: "Directo" },
];

// ============================================================
// FASE 4 — Emails / Subscribers / Campaigns / Analytics
// ============================================================
export const listEmails = (params = {}) =>
    adminApi.get("/emails", { params }).then((r) => r.data);
export const emailsStats = () => adminApi.get("/emails/stats").then((r) => r.data);
export const leadEmails = (id) => adminApi.get(`/leads/${id}/emails`).then((r) => r.data);
export const sendManualEmail = (id, body) =>
    adminApi.post(`/leads/${id}/send-email`, body).then((r) => r.data);
export const sendTestEmail = (body) => adminApi.post("/test-email", body).then((r) => r.data);

export const listSubscribers = (params = {}) =>
    adminApi.get("/subscribers", { params }).then((r) => r.data);
export const subscribersStats = () => adminApi.get("/subscribers/stats").then((r) => r.data);
export const createSubscriber = (body) =>
    adminApi.post("/subscribers", body).then((r) => r.data);
export const deleteSubscriber = (id) =>
    adminApi.delete(`/subscribers/${id}`).then((r) => r.data);
export const uploadSubscribersCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await adminApi.post("/subscribers/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
};
export const downloadSubscribersCsv = async () => {
    const res = await adminApi.get("/subscribers/export/csv", { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "eclipse_subscribers.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const listCampaigns = () => adminApi.get("/campaigns").then((r) => r.data);
export const createCampaign = (body) => adminApi.post("/campaigns", body).then((r) => r.data);
export const campaignDetail = (id) => adminApi.get(`/campaigns/${id}`).then((r) => r.data);
export const patchCampaign = (id, body) =>
    adminApi.patch(`/campaigns/${id}`, body).then((r) => r.data);
export const deleteCampaign = (id) => adminApi.delete(`/campaigns/${id}`).then((r) => r.data);
export const previewAudience = (id) =>
    adminApi.post(`/campaigns/${id}/preview-audience`).then((r) => r.data);
export const sendCampaignNow = (id) =>
    adminApi.post(`/campaigns/${id}/send-now`).then((r) => r.data);
export const sendCampaignTest = (id, test_emails) =>
    adminApi.post(`/campaigns/${id}/test`, { test_emails }).then((r) => r.data);
export const scheduleCampaign = (id, scheduled_at) =>
    adminApi.post(`/campaigns/${id}/schedule`, { scheduled_at }).then((r) => r.data);

export const analyticsSummary = (days = 30, sitio) =>
    adminApi.get("/analytics/summary", { params: { days, sitio } }).then((r) => r.data);
export const analyticsFunnel = (days = 30, sitio) =>
    adminApi.get("/analytics/funnel", { params: { days, sitio } }).then((r) => r.data);
export const analyticsSources = (days = 30, sitio) =>
    adminApi.get("/analytics/sources", { params: { days, sitio } }).then((r) => r.data);
export const analyticsCampaigns = (days = 30) =>
    adminApi.get("/analytics/campaigns", { params: { days } }).then((r) => r.data);
export const analyticsDaily = (days = 30, sitio) =>
    adminApi.get("/analytics/daily", { params: { days, sitio } }).then((r) => r.data);

export const EMAIL_TYPE_OPTIONS = [
    { value: "lead_confirmation", label: "Lead — Confirmación" },
    { value: "lead_catalog_delivery", label: "Lead — Catálogo" },
    { value: "lead_followup_48h", label: "Lead — Follow-up 48h" },
    { value: "lead_followup_hot_24h", label: "Lead — Follow-up HOT 24h" },
    { value: "team_new_lead", label: "Equipo — Nuevo lead" },
    { value: "team_catalog_download", label: "Equipo — Catálogo" },
    { value: "team_uncontacted_alert", label: "Equipo — Alerta sin contactar" },
    { value: "campaign", label: "Campaña" },
    { value: "campaign_test", label: "Campaña — Test" },
    { value: "manual", label: "Manual" },
    { value: "test", label: "Test" },
];

export const EMAIL_STATUS = {
    sent: { label: "Enviado", color: "bg-green-100 text-green-700 border-green-200" },
    failed: { label: "Fallido", color: "bg-red-100 text-red-700 border-red-200" },
    skipped: { label: "Skipped", color: "bg-neutral-100 text-neutral-600 border-neutral-200" },
    pending: { label: "Pendiente", color: "bg-amber-100 text-amber-700 border-amber-200" },
};
